<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="innerVisible"
    width="860px"
    append-to-body
    destroy-on-close
    custom-class="device-trend-dialog"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div class="trend-toolbar">
      <div class="device-info">
        <span class="name">{{ terminalName || '—' }}</span>
        <span class="code">编码 {{ terminalCode || '—' }}</span>
      </div>
      <el-radio-group v-model="rangeHours" size="small" @change="loadTrend">
        <el-radio-button :label="24">24小时</el-radio-button>
        <el-radio-button :label="168">7天</el-radio-button>
        <el-radio-button :label="720">30天</el-radio-button>
      </el-radio-group>
    </div>

    <div v-loading="chartLoading" class="chart-panel">
      <div class="chart-head">
        <div>
          <div class="chart-title">{{ chartTitle }}</div>
          <div class="chart-sub">{{ rangeLabel }}</div>
        </div>
        <div v-if="latestValue != null" class="latest">
          当前值 <strong>{{ latestValue }}</strong> {{ unit }}
        </div>
      </div>
      <div class="chart-stage">
        <div ref="chartEl" class="chart-box"></div>
        <div v-if="!chartLoading && !pointCount" class="empty-chart">暂无真实趋势数据</div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import * as echarts from 'echarts'
import { gasApi } from '@/api/gas'
import { metricByKey } from '@/config/deviceTrendMetrics'

export default {
  name: 'DeviceTrendDialog',
  props: {
    visible: { type: Boolean, default: false },
    deviceId: { type: [Number, String], default: null },
    deviceCode: { type: String, default: '' },
    terminalCode: { type: String, default: '' },
    terminalName: { type: String, default: '' },
    subDeviceCode: { type: String, default: '' },
    metric: { type: String, default: 'stdInstant' }
  },
  data() {
    return {
      rangeHours: 24,
      chartLoading: false,
      trendData: null,
      chart: null
    }
  },
  computed: {
    innerVisible: {
      get() { return this.visible },
      set(v) { this.$emit('update:visible', v) }
    },
    unit() {
      return (this.trendData && this.trendData.unit) || metricByKey(this.metric).unit
    },
    metricLabel() {
      return (this.trendData && this.trendData.metricLabel) || metricByKey(this.metric).label
    },
    chartTitle() {
      return `${this.metricLabel}趋势`
    },
    dialogTitle() {
      return `${this.metricLabel}趋势图`
    },
    rangeLabel() {
      if (this.rangeHours === 168) return '近7天'
      if (this.rangeHours === 720) return '近30天'
      return '近24小时'
    },
    latestValue() {
      const pts = (this.trendData && this.trendData.points) || []
      for (let i = pts.length - 1; i >= 0; i--) {
        if (pts[i] && pts[i].value != null) return pts[i].value
      }
      return null
    },
    pointCount() {
      return ((this.trendData && this.trendData.points) || []).length
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.rangeHours = 24
        this.trendData = null
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    this.disposeChart()
  },
  methods: {
    onOpened() {
      window.addEventListener('resize', this.resizeChart)
      this.loadTrend()
    },
    onClosed() {
      window.removeEventListener('resize', this.resizeChart)
      this.disposeChart()
      this.trendData = null
    },
    disposeChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },
    async loadTrend() {
      this.chartLoading = true
      try {
        const params = {
          metric: this.metric,
          rangeHours: this.rangeHours
        }
        if (this.deviceId) params.deviceId = this.deviceId
        if (this.deviceCode) params.deviceCode = this.deviceCode
        if (this.terminalCode) params.terminalCode = this.terminalCode
        if (this.subDeviceCode) params.subDeviceCode = this.subDeviceCode
        const res = await gasApi.terminal.monitorTrend(params)
        this.trendData = res.data || {}
        this.$nextTick(() => this.renderChart())
      } finally {
        this.chartLoading = false
      }
    },
    renderChart() {
      const el = this.$refs.chartEl
      if (!el) return
      if (!this.chart) this.chart = echarts.init(el)
      const pts = (this.trendData && this.trendData.points) || []
      if (!pts.length) {
        this.chart.clear()
        return
      }
      const labels = pts.map(p => p.label)
      const values = pts.map(p => (p.value == null ? null : Number(p.value)))
      this.chart.setOption({
        grid: { left: 48, right: 24, top: 32, bottom: 40 },
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            const item = Array.isArray(params) ? params[0] : params
            const val = item && item.value
            return `${item.axisValue}<br/>${item.marker}${val == null ? '无上报数据' : val}`
          }
        },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: '#e5e6eb' } },
          axisLabel: { color: '#86909c', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          name: this.unit,
          axisLine: { show: false },
          splitLine: { lineStyle: { color: '#f2f3f5' } },
          axisLabel: { color: '#86909c' }
        },
        series: [{
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          data: values,
          lineStyle: { width: 2, color: '#1677ff' },
          itemStyle: { color: '#1677ff' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(22,119,255,0.25)' },
              { offset: 1, color: 'rgba(22,119,255,0.02)' }
            ])
          }
        }]
      }, true)
      this.resizeChart()
    },
    resizeChart() {
      if (this.chart) this.chart.resize()
    }
  }
}
</script>

<style scoped lang="scss">
.trend-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.device-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.device-info .name {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.device-info .code {
  font-size: 12px;
  color: #86909c;
}
.chart-panel {
  background: #fafbfc;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 14px 16px 8px;
}
.chart-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 12px;
}
.chart-title { font-size: 15px; font-weight: 600; color: #1d2129; }
.chart-sub { font-size: 12px; color: #86909c; margin-top: 4px; }
.latest { font-size: 13px; color: #86909c; }
.latest strong { font-size: 20px; color: #1677ff; margin: 0 4px; }
.chart-stage { position: relative; }
.chart-box { width: 100%; height: 360px; }
.empty-chart {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #86909c;
  font-size: 13px;
  background: #fafbfc;
}
</style>
